import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Users, UserCheck, UserX, TrendingUp } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatCard = ({ title, value, icon: Icon, color, trend }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white rounded-xl border border-slate-100 p-6 relative overflow-hidden group hover:shadow-md transition-all"
  >
    <div className="flex items-start justify-between mb-3">
      <div>
        <p className="text-sm text-slate-500 mb-1">{title}</p>
        <h3 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif', color }}>
          {value}
        </h3>
      </div>
      <div className={`p-3 rounded-xl bg-gradient-to-br ${color === '#7c3aed' ? 'from-violet-100 to-purple-100' : color === '#10b981' ? 'from-emerald-100 to-green-100' : 'from-red-100 to-pink-100'}`}>
        <Icon size={24} style={{ color }} />
      </div>
    </div>
    {trend && (
      <div className="flex items-center gap-1 text-sm text-emerald-600">
        <TrendingUp size={14} />
        <span>{trend}</span>
      </div>
    )}
  </motion.div>
);

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-violet-200 border-t-violet-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard" className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Dashboard
          </h1>
          <p className="text-slate-600">Welcome to GPOS Admin Portal</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Tenants"
          value={stats?.total_tenants || 0}
          icon={Users}
          color="#7c3aed"
          trend="+12% this month"
        />
        <StatCard
          title="Active Tenants"
          value={stats?.active_tenants || 0}
          icon={UserCheck}
          color="#10b981"
        />
        <StatCard
          title="Inactive Tenants"
          value={stats?.inactive_tenants || 0}
          icon={UserX}
          color="#ef4444"
        />
        <StatCard
          title="Total Revenue"
          value={`₹${stats?.total_revenue?.toLocaleString('en-IN') || 0}`}
          icon={TrendingUp}
          color="#f97316"
          trend="+18% this month"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl border border-slate-100 p-6"
        >
          <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Tenant Growth
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={stats?.tenants_growth || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" stroke="#64748b" />
              <YAxis stroke="#64748b" />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                }}
              />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#7c3aed"
                strokeWidth={3}
                dot={{ fill: '#7c3aed', r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl border border-slate-100 p-6"
        >
          <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Revenue Growth
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={stats?.revenue_growth || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" stroke="#64748b" />
              <YAxis stroke="#64748b" />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                }}
                formatter={(value) => [`₹${value.toLocaleString('en-IN')}`, 'Revenue']}
              />
              <Line
                type="monotone"
                dataKey="revenue"
                stroke="#f97316"
                strokeWidth={3}
                dot={{ fill: '#f97316', r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;